/**
 * Anotações do autor sobre uma captura: destaques (rectângulos) e setas. Guardadas no passo
 * como vetores (fração da captura inteira), desenhadas por cima da imagem ao mostrar e nas
 * exportações. A imagem não muda: apagar uma anotação não perde nada.
 */
import type { Box, Point } from './steps';

export type AnnotationColor = 'red' | 'orange' | 'blue' | 'green';

export const ANNOTATION_COLORS: Record<AnnotationColor, { label: string; hex: string }> = {
  red: { label: 'Vermelho', hex: '#E5484D' },
  orange: { label: 'Laranja', hex: '#F76B15' },
  blue: { label: 'Azul', hex: '#0B6FE0' },
  green: { label: 'Verde', hex: '#1F9D55' },
};

export type Annotation =
  | ({ kind: 'box'; color: AnnotationColor } & Box)
  | { kind: 'arrow'; color: AnnotationColor; from: Point; to: Point };

/** Máximo de anotações por passo (a API recusa mais). */
export const MAX_ANNOTATIONS = 30;

export const annotationHex = (a: Pick<Annotation, 'color'>) =>
  (ANNOTATION_COLORS[a.color] ?? ANNOTATION_COLORS.red).hex;

/** Passa uma anotação da captura inteira para o recorte mostrado (pode sair de 0…1). */
export function annotationInCrop(a: Annotation, crop: Box): Annotation {
  const px = (p: Point): Point => ({ x: (p.x - crop.x) / crop.w, y: (p.y - crop.y) / crop.h });
  if (a.kind === 'arrow') return { ...a, from: px(a.from), to: px(a.to) };
  const p = px(a);
  return { ...a, x: p.x, y: p.y, w: a.w / crop.w, h: a.h / crop.h };
}

/** Espessura do traço em píxeis, proporcional à imagem mostrada. */
export function annotationStroke(w: number, h: number): number {
  return Math.max(2.5, Math.min(w, h) * 0.008);
}

/**
 * Geometria de uma seta em píxeis: o traço acaba onde começa a ponta (para a ponta ficar
 * bem aguçada) e a ponta é um triângulo com a ponta em `to`.
 */
export function arrowGeometry(
  from: Point,
  to: Point,
  stroke: number,
): { shaftEnd: Point; head: [Point, Point, Point] } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const size = Math.min(stroke * 4.5, len * 0.6);
  const base = { x: to.x - ux * size, y: to.y - uy * size };
  const half = size * 0.55;
  return {
    shaftEnd: { x: to.x - ux * size * 0.6, y: to.y - uy * size * 0.6 },
    head: [
      { x: to.x, y: to.y },
      { x: base.x - uy * half, y: base.y + ux * half },
      { x: base.x + uy * half, y: base.y - ux * half },
    ],
  };
}
